import React, { useState, useEffect, useCallback } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isToday,
  isBefore,
  parseISO,
  setHours,
  setMinutes,
} from "date-fns";
import el from "date-fns/locale/el";
import {
  ChevronLeft,
  ChevronRight,
  Lock,
  Unlock,
  Calendar,
  LogOut,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { adminApi, TimeSlot } from "../services/api";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";

// ─── Types ───────────────────────────────────────────────
interface DayInfo {
  date: Date;
  available: number;
  booked: number;
  blocked: number;
  total: number;
}

interface Toast {
  id: number;
  message: string;
  type: "success" | "error";
}

// ─── Slot hours definition ──────────────────────────────
const SLOT_HOURS = [
  { hour: 16, minute: 0 },
  { hour: 16, minute: 30 },
  { hour: 17, minute: 0 },
  { hour: 17, minute: 30 },
  { hour: 18, minute: 0 },
  { hour: 18, minute: 30 },
  { hour: 19, minute: 0 },
  { hour: 19, minute: 30 },
  { hour: 20, minute: 0 },
];

// ─── Toast Component ────────────────────────────────────
const ToastContainer: React.FC<{
  toasts: Toast[];
  onRemove: (id: number) => void;
}> = ({ toasts, onRemove }) => (
  <div
    style={{
      position: "fixed",
      top: 20,
      right: 20,
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}
  >
    {toasts.map((t) => (
      <div
        key={t.id}
        onClick={() => onRemove(t.id)}
        style={{
          padding: "12px 20px",
          borderRadius: 8,
          backgroundColor: t.type === "success" ? "#d0ddc6" : "#fecaca",
          color: t.type === "success" ? "#1f2937" : "#991b1b",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 8,
          animation: "fadeIn 0.3s ease",
          maxWidth: 360,
        }}
      >
        {t.type === "success" ? (
          <CheckCircle2 size={18} />
        ) : (
          <XCircle size={18} />
        )}
        {t.message}
      </div>
    ))}
  </div>
);

// ─── Confirmation Dialog ────────────────────────────────
const ConfirmDialog: React.FC<{
  open: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, message, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9998,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          padding: 28,
          maxWidth: 400,
          width: "90%",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={24} color="#a64f3c" />
          <h3 style={{ margin: 0, fontSize: 18, color: "#1f2937" }}>
            Επιβεβαίωση
          </h3>
        </div>
        <p
          style={{
            color: "#4b5563",
            fontSize: 15,
            lineHeight: 1.5,
            margin: "0 0 24px",
          }}
        >
          {message}
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "white",
              color: "#4b5563",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Ακύρωση
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#a64f3c",
              color: "white",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Επιβεβαίωση
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────
export const AdminPanel: React.FC = () => {
  const [token, setToken] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [tokenInput, setTokenInput] = useState("");

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [allSlots, setAllSlots] = useState<TimeSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  // ─── Toast helpers ──────────────────────────────────
  const addToast = useCallback((message: string, type: "success" | "error") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4000,
    );
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ─── Auth ───────────────────────────────────────────
  useEffect(() => {
    const savedToken = sessionStorage.getItem("admin_token");
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async () => {
    if (!tokenInput.trim()) {
      setLoginError("Παρακαλώ εισάγετε τον κωδικό.");
      return;
    }
    try {
      setIsLoading(true);
      const now = new Date();
      const start = startOfMonth(addMonths(now, 1));
      const end = endOfMonth(addMonths(now, 2));
      await adminApi.listSlots(start, end, tokenInput);
      sessionStorage.setItem("admin_token", tokenInput);
      setToken(tokenInput);
      setIsAuthenticated(true);
      setLoginError("");
    } catch {
      setLoginError("Λάθος κωδικός διαχείρισης. Δοκιμάστε ξανά.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
    setIsAuthenticated(false);
    setTokenInput("");
  };

  // ─── Data loading ───────────────────────────────────
  const loadSlots = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      // Extend range a bit for calendar padding
      const paddedStart = startOfWeek(start, { weekStartsOn: 1 });
      const paddedEnd = endOfWeek(end, { weekStartsOn: 1 });
      const slots = await adminApi.listSlots(
        paddedStart,
        addDays(paddedEnd, 1),
        token,
      );
      setAllSlots(Array.isArray(slots) ? slots : []);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        handleLogout();
      } else {
        addToast("Σφάλμα φόρτωσης δεδομένων.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, currentMonth, addToast]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSlots();
    }
  }, [isAuthenticated, loadSlots]);

  // ─── Calendar helpers ──────────────────────────────
  const getDayInfo = useCallback(
    (date: Date): DayInfo => {
      const dateStr = format(date, "yyyy-MM-dd");
      const daySlots = allSlots.filter((s) => {
        const slotDate = format(parseISO(s.slot_datetime), "yyyy-MM-dd");
        return slotDate === dateStr;
      });
      const available = daySlots.filter((s) => s.is_available).length;
      // Use server-derived status when present; otherwise fall back to treating
      // every not-available slot as booked (pre-status-field backend).
      const booked = daySlots.filter((s) =>
        s.status ? s.status === "booked" : !s.is_available,
      ).length;
      const blocked = daySlots.filter((s) => s.status === "blocked").length;
      return { date, available, booked, blocked, total: daySlots.length };
    },
    [allSlots],
  );

  const getDaySlots = useCallback(
    (date: Date): TimeSlot[] => {
      const dateStr = format(date, "yyyy-MM-dd");
      return allSlots
        .filter(
          (s) => format(parseISO(s.slot_datetime), "yyyy-MM-dd") === dateStr,
        )
        .sort((a, b) => a.slot_datetime.localeCompare(b.slot_datetime));
    },
    [allSlots],
  );

  // ─── Slot actions ──────────────────────────────────
  const toggleSlot = async (slot: TimeSlot) => {
    try {
      const dt = parseISO(slot.slot_datetime);
      const start = dt;
      const end = new Date(dt.getTime() + 30 * 60 * 1000);

      if (slot.is_available) {
        await adminApi.blockRange(start, end, token);
        addToast(`Slot ${format(dt, "HH:mm")} μπλοκαρίστηκε.`, "success");
      } else {
        await adminApi.createRange(start, end, token);
        addToast(`Slot ${format(dt, "HH:mm")} ξεκλειδώθηκε.`, "success");
      }
      await loadSlots();
    } catch {
      addToast("Σφάλμα κατά την ενημέρωση slot.", "error");
    }
  };

  const createSlotIfMissing = async (
    date: Date,
    hour: number,
    minute: number,
  ) => {
    const dt = setMinutes(setHours(new Date(date), hour), minute);
    dt.setSeconds(0);
    dt.setMilliseconds(0);
    const end = new Date(dt.getTime() + 30 * 60 * 1000);
    try {
      await adminApi.createRange(dt, end, token);
      addToast(`Slot ${format(dt, "HH:mm")} δημιουργήθηκε.`, "success");
      await loadSlots();
    } catch {
      addToast("Σφάλμα δημιουργίας slot.", "error");
    }
  };

  const openDay = async (date: Date) => {
    const start = setMinutes(setHours(new Date(date), 16), 0);
    start.setSeconds(0);
    start.setMilliseconds(0);
    const end = setMinutes(setHours(new Date(date), 20), 0);
    end.setSeconds(0);
    end.setMilliseconds(0);
    try {
      await adminApi.createRange(start, end, token);
      addToast(
        `Ξεκλειδώθηκαν όλα τα slots για ${format(date, "dd/MM")}.`,
        "success",
      );
      await loadSlots();
    } catch {
      addToast("Σφάλμα ξεκλειδώματος ημέρας.", "error");
    }
  };

  const blockDay = async (date: Date) => {
    const start = setMinutes(setHours(new Date(date), 0), 0);
    start.setSeconds(0);
    start.setMilliseconds(0);
    const end = setMinutes(setHours(new Date(date), 23), 59);
    end.setSeconds(0);
    end.setMilliseconds(0);
    try {
      await adminApi.blockRange(start, end, token);
      addToast(
        `Μπλοκαρίστηκαν όλα τα slots για ${format(date, "dd/MM")}.`,
        "success",
      );
      await loadSlots();
    } catch {
      addToast("Σφάλμα μπλοκαρίσματος ημέρας.", "error");
    }
  };

  const openWeek = async (date: Date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 5); // Mon to Fri
    try {
      for (let d = new Date(weekStart); d < weekEnd; d = addDays(d, 1)) {
        if (d.getDay() !== 0 && d.getDay() !== 6 && d.getDay() !== 3) {
          const s = setMinutes(setHours(new Date(d), 16), 0);
          s.setSeconds(0);
          s.setMilliseconds(0);
          const e = setMinutes(setHours(new Date(d), 20), 0);
          e.setSeconds(0);
          e.setMilliseconds(0);
          await adminApi.createRange(s, e, token);
        }
      }
      addToast(
        `Ξεκλειδώθηκε η εβδομάδα ${format(weekStart, "dd/MM")} - ${format(addDays(weekStart, 4), "dd/MM")}.`,
        "success",
      );
      await loadSlots();
    } catch {
      addToast("Σφάλμα ξεκλειδώματος εβδομάδας.", "error");
    }
  };

  const blockWeek = async (date: Date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 5);
    try {
      const s = new Date(weekStart);
      s.setHours(0, 0, 0, 0);
      const e = new Date(weekEnd);
      e.setHours(23, 59, 0, 0);
      await adminApi.blockRange(s, e, token);
      addToast(
        `Μπλοκαρίστηκε η εβδομάδα ${format(weekStart, "dd/MM")} - ${format(addDays(weekStart, 4), "dd/MM")}.`,
        "success",
      );
      await loadSlots();
    } catch {
      addToast("Σφάλμα μπλοκαρίσματος εβδομάδας.", "error");
    }
  };

  // ─── Stats ─────────────────────────────────────────
  const monthSlots = allSlots.filter((s) => {
    const d = parseISO(s.slot_datetime);
    return isSameMonth(d, currentMonth);
  });
  const totalAvailable = monthSlots.filter((s) => s.is_available).length;
  const totalBooked = monthSlots.filter((s) =>
    s.status ? s.status === "booked" : !s.is_available,
  ).length;
  const totalBlocked = monthSlots.filter((s) => s.status === "blocked").length;
  // Utilization = booked / bookable (available + booked); blocked slots are
  // excluded from the denominator so closing a day doesn't inflate the figure.
  const bookable = totalAvailable + totalBooked;
  const utilization =
    bookable > 0 ? Math.round((totalBooked / bookable) * 100) : 0;

  // ─── Calendar grid ─────────────────────────────────
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days: Date[] = [];
    let day = calStart;
    while (day <= calEnd) {
      days.push(day);
      day = addDays(day, 1);
    }

    const dayNames = ["Δευ", "Τρι", "Τετ", "Πεμ", "Παρ", "Σαβ", "Κυρ"];

    return (
      <div>
        {/* Day names header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
            marginBottom: 4,
          }}
        >
          {dayNames.map((name) => (
            <div
              key={name}
              style={{
                textAlign: "center",
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
                padding: "6px 0",
              }}
            >
              {name}
            </div>
          ))}
        </div>
        {/* Day cells */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
          }}
        >
          {days.map((d, i) => {
            const info = getDayInfo(d);
            const inMonth = isSameMonth(d, currentMonth);
            const today = isToday(d);
            const pastDay = isBefore(d, new Date()) && !today;
            const isSelected =
              selectedDate &&
              format(d, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;

            return (
              <div
                key={i}
                onClick={() => !isWeekend && setSelectedDate(d)}
                style={{
                  minHeight: 72,
                  padding: 6,
                  borderRadius: 8,
                  backgroundColor: isSelected
                    ? "#d0ddc6"
                    : today
                      ? "#f0f9eb"
                      : !inMonth || isWeekend
                        ? "#f9fafb"
                        : "white",
                  border: isSelected
                    ? "2px solid #1f2937"
                    : today
                      ? "2px solid #d0ddc6"
                      : "1px solid #e5e7eb",
                  cursor: isWeekend ? "default" : "pointer",
                  opacity:
                    pastDay && !inMonth
                      ? 0.4
                      : pastDay
                        ? 0.6
                        : !inMonth
                          ? 0.5
                          : 1,
                  transition: "all 0.15s ease",
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: today ? 700 : 500,
                    color: inMonth ? "#1f2937" : "#9ca3af",
                    marginBottom: 4,
                  }}
                >
                  {format(d, "d")}
                </div>
                {info.total > 0 && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {info.available > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          backgroundColor: "#d0ddc6",
                          color: "#1f2937",
                          padding: "1px 6px",
                          borderRadius: 10,
                        }}
                      >
                        {info.available}
                      </span>
                    )}
                    {info.booked > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          backgroundColor: "#fed7aa",
                          color: "#92400e",
                          padding: "1px 6px",
                          borderRadius: 10,
                        }}
                      >
                        {info.booked}
                      </span>
                    )}
                    {info.blocked > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          backgroundColor: "#e5e7eb",
                          color: "#6b7280",
                          padding: "1px 6px",
                          borderRadius: 10,
                        }}
                      >
                        {info.blocked}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Day detail panel ──────────────────────────────
  const renderDayDetail = () => {
    if (!selectedDate)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#9ca3af",
            fontSize: 15,
          }}
        >
          <Calendar size={20} style={{ marginRight: 8 }} />
          Επιλέξτε μια ημέρα από το ημερολόγιο
        </div>
      );

    const daySlots = getDaySlots(selectedDate);
    const slotMap = new Map(
      daySlots.map((s) => [format(parseISO(s.slot_datetime), "HH:mm"), s]),
    );

    return (
      <div>
        {/* Day header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#1f2937",
            }}
          >
            {format(selectedDate, "EEEE, d MMMM yyyy", { locale: el })}
          </h3>
        </div>

        {/* Day quick actions */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() =>
              setConfirmDialog({
                open: true,
                message: `Θέλετε να ξεκλειδώσετε όλα τα slots για ${format(selectedDate, "dd/MM/yyyy")};`,
                onConfirm: () => {
                  openDay(selectedDate);
                  setConfirmDialog((prev) => ({ ...prev, open: false }));
                },
              })
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #d0ddc6",
              background: "#f0f9eb",
              color: "#1f2937",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Unlock size={15} /> Ξεκλείδωμα ημέρας
          </button>
          <button
            onClick={() =>
              setConfirmDialog({
                open: true,
                message: `Θέλετε να μπλοκάρετε όλα τα slots για ${format(selectedDate, "dd/MM/yyyy")};`,
                onConfirm: () => {
                  blockDay(selectedDate);
                  setConfirmDialog((prev) => ({ ...prev, open: false }));
                },
              })
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f3f4f6",
              color: "#4b5563",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Lock size={15} /> Μπλοκάρισμα ημέρας
          </button>
        </div>

        {/* Slot grid */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {SLOT_HOURS.map(({ hour, minute }) => {
            const timeStr = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
            const slot = slotMap.get(timeStr);

            if (slot) {
              const st =
                slot.status ?? (slot.is_available ? "available" : "unknown");
              const meta = {
                available: {
                  rowBg: "#f0f9eb",
                  rowBorder: "#d0ddc6",
                  pillBg: "#d0ddc6",
                  pillColor: "#1f2937",
                  label: "Διαθέσιμο",
                },
                booked: {
                  rowBg: "#fff7ed",
                  rowBorder: "#fed7aa",
                  pillBg: "#fed7aa",
                  pillColor: "#92400e",
                  label: "Κλεισμένο – ραντεβού",
                },
                blocked: {
                  rowBg: "#f3f4f6",
                  rowBorder: "#e5e7eb",
                  pillBg: "#e5e7eb",
                  pillColor: "#6b7280",
                  label: "Μπλοκαρισμένο",
                },
                unknown: {
                  rowBg: "#f3f4f6",
                  rowBorder: "#e5e7eb",
                  pillBg: "#e5e7eb",
                  pillColor: "#6b7280",
                  label: "μη Διαθέσιμο",
                },
              }[st];
              const canUnblock = st === "blocked" || st === "unknown";
              return (
                <div
                  key={timeStr}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderRadius: 8,
                    backgroundColor: meta.rowBg,
                    border: `1px solid ${meta.rowBorder}`,
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#1f2937",
                        fontFamily: "monospace",
                        minWidth: 50,
                      }}
                    >
                      {timeStr}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        padding: "3px 10px",
                        borderRadius: 12,
                        backgroundColor: meta.pillBg,
                        color: meta.pillColor,
                      }}
                    >
                      {meta.label}
                    </span>
                  </div>
                  {st === "available" && (
                    <button
                      onClick={() => toggleSlot(slot)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        minHeight: 38,
                        minWidth: 44,
                        backgroundColor: "#f3f4f6",
                        color: "#4b5563",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Lock size={14} /> Μπλοκάρισμα
                    </button>
                  )}
                  {canUnblock && (
                    <button
                      onClick={() => toggleSlot(slot)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        minHeight: 38,
                        minWidth: 44,
                        backgroundColor: "#d0ddc6",
                        color: "#1f2937",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Unlock size={14} /> Ξεκλείδωμα
                    </button>
                  )}
                  {st === "booked" && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#92400e",
                        fontStyle: "italic",
                      }}
                    >
                      Κλεισμένο ραντεβού
                    </span>
                  )}
                </div>
              );
            }

            // Slot doesn't exist yet — show create option
            return (
              <div
                key={timeStr}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  borderRadius: 8,
                  backgroundColor: "#fafafa",
                  border: "1px dashed #d1d5db",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#9ca3af",
                      fontFamily: "monospace",
                      minWidth: 50,
                    }}
                  >
                    {timeStr}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#9ca3af",
                      fontStyle: "italic",
                    }}
                  >
                    Δεν υπάρχει
                  </span>
                </div>
                <button
                  onClick={() =>
                    createSlotIfMissing(selectedDate, hour, minute)
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #d0ddc6",
                    background: "white",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1f2937",
                    minHeight: 38,
                  }}
                >
                  + Δημιουργία
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Login Screen ──────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <Card style={{ maxWidth: 400, width: "100%" }}>
          <CardHeader
            style={{ backgroundColor: "#d0ddc6", borderRadius: "8px 8px 0 0" }}
          >
            <CardTitle
              style={{ textAlign: "center", color: "#1f2937", fontSize: 20 }}
            >
              <Lock
                size={20}
                style={{
                  display: "inline",
                  marginRight: 8,
                  verticalAlign: "middle",
                }}
              />
              Πίνακας Διαχείρισης
            </CardTitle>
          </CardHeader>
          <CardContent style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1f2937",
                  marginBottom: 6,
                }}
              >
                Κωδικός Διαχείρισης
              </label>
              <Input
                type="password"
                value={tokenInput}
                onChange={(e) => {
                  setTokenInput(e.target.value);
                  setLoginError("");
                }}
                onKeyPress={(e) => e.key === "Enter" && handleLogin()}
                placeholder="Εισάγετε τον κωδικό..."
                style={{ fontSize: 16 }}
              />
            </div>
            {loginError && (
              <p
                style={{
                  color: "#dc2626",
                  fontSize: 14,
                  margin: "0 0 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <XCircle size={16} /> {loginError}
              </p>
            )}
            <Button
              onClick={handleLogin}
              disabled={isLoading}
              style={{
                width: "100%",
                backgroundColor: "#1f2937",
                color: "white",
                fontSize: 15,
                padding: "10px 0",
                fontWeight: 600,
              }}
            >
              {isLoading ? "Έλεγχος..." : "Είσοδος"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Main Admin Panel ──────────────────────────────
  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 16px" }}>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h2
          style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1f2937" }}
        >
          📅 Διαχείριση Ραντεβού
        </h2>
        <button
          onClick={handleLogout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "white",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 500,
            color: "#6b7280",
          }}
        >
          <LogOut size={15} /> Αποσύνδεση
        </button>
      </div>

      {/* Week quick actions */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
          padding: "12px 16px",
          backgroundColor: "#f9fafb",
          borderRadius: 10,
          border: "1px solid #e5e7eb",
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#4b5563",
            display: "flex",
            alignItems: "center",
            marginRight: 8,
          }}
        >
          Γρήγορες ενέργειες:
        </span>
        <button
          onClick={() => {
            if (!selectedDate) {
              addToast("Επιλέξτε πρώτα μια ημέρα.", "error");
              return;
            }
            setConfirmDialog({
              open: true,
              message: `Θέλετε να ανοίξετε όλα τα slots για την εβδομάδα της ${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "dd/MM")};`,
              onConfirm: () => {
                openWeek(selectedDate);
                setConfirmDialog((prev) => ({ ...prev, open: false }));
              },
            });
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #d0ddc6",
            background: "#f0f9eb",
            color: "#1f2937",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Unlock size={15} /> Άνοιγμα Εβδομάδας
        </button>
        <button
          onClick={() => {
            if (!selectedDate) {
              addToast("Επιλέξτε πρώτα μια ημέρα.", "error");
              return;
            }
            setConfirmDialog({
              open: true,
              message: `Θέλετε να μπλοκάρετε όλα τα slots για την εβδομάδα της ${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "dd/MM")};`,
              onConfirm: () => {
                blockWeek(selectedDate);
                setConfirmDialog((prev) => ({ ...prev, open: false }));
              },
            });
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#f3f4f6",
            color: "#4b5563",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Lock size={15} /> Μπλοκάρισμα Εβδομάδας
        </button>
      </div>

      {/* Main layout: Calendar + Detail */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        {/* Calendar */}
        <Card>
          <CardContent style={{ padding: 16 }}>
            {/* Month navigation */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <h3
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1f2937",
                }}
              >
                {format(currentMonth, "LLLL yyyy", { locale: el })}
              </h3>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Loading indicator */}
            {isLoading && (
              <div
                style={{
                  textAlign: "center",
                  padding: 8,
                  color: "#6b7280",
                  fontSize: 13,
                }}
              >
                Φόρτωση...
              </div>
            )}

            {renderCalendar()}

            {/* Legend */}
            <div
              style={{
                display: "flex",
                gap: 16,
                marginTop: 12,
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: "#d0ddc6",
                    display: "inline-block",
                  }}
                />
                Διαθέσιμα
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: "#fed7aa",
                    display: "inline-block",
                  }}
                />
                Κλεισμένα
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: "#e5e7eb",
                    display: "inline-block",
                  }}
                />
                Μπλοκαρισμένα
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Day detail */}
        <Card style={{ minHeight: 400 }}>
          <CardContent style={{ padding: 16 }}>{renderDayDetail()}</CardContent>
        </Card>
      </div>

      {/* Stats footer */}
      <div
        style={{
          marginTop: 20,
          padding: "14px 20px",
          backgroundColor: "#f9fafb",
          borderRadius: 10,
          border: "1px solid #e5e7eb",
          display: "flex",
          gap: 32,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1f2937" }}>
            {totalAvailable}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>Διαθέσιμα</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#92400e" }}>
            {totalBooked}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>Κλεισμένα</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#6b7280" }}>
            {totalBlocked}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>Μπλοκαρισμένα</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1f2937" }}>
            {utilization}%
          </div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>Πληρότητα</div>
        </div>
      </div>
    </div>
  );
};
